import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Building2, Users, CheckCircle2, ArrowRight, ClipboardList, Moon, Sun, FileText, TrendingUp, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/hooks/useTheme';
import { loadFeatureFlags } from '@/components/utils/featureFlags';

export default function Hr() {
  const { isDark, toggleTheme } = useTheme();
  const [featureFlags, setFeatureFlags] = useState({
    allow_company_registration: true
  });

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      const flags = await loadFeatureFlags();
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">BenefitsPro</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" onClick={() => (window.location.href = '/')}>
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 text-sm px-4 py-2">
            <Building2 className="w-4 h-4 mr-2" />
            For HR & Benefits Teams
          </Badge>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
            Run enrollment without the chaos
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Set up Section 125 benefits, invite employees, and track enrollment progress — all in one place.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            {featureFlags.allow_company_registration && (
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-lg px-8 py-6"
                onClick={() => (window.location.href = '/CompanySignup')}
              >
                Get started for my company
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6"
              onClick={() => api.auth.redirectToLogin()}
            >
              Sign in
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                <ClipboardList className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Guided setup</h3>
              <p className="text-muted-foreground">
                Onboard your company, configure plans, and stay organized through each step.
              </p>
            </CardContent>
          </Card>

          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Employee invites</h3>
              <p className="text-muted-foreground">
                Invite employees and let them complete enrollment in a clean, self-serve portal.
              </p>
            </CardContent>
          </Card>

          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Track progress</h3>
              <p className="text-muted-foreground">
                Monitor enrollment status and follow up with employees who still need to complete steps.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="mt-10 bg-gradient-to-br from-emerald-500 to-teal-500 border-0">
            <CardContent className="p-10 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Want the overview first?</h2>
              <p className="text-white/90 text-lg mb-6">
                Learn what BenefitsPro is and why companies adopt it.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <Button variant="secondary" onClick={() => (window.location.href = '/why')}>
                  Why it matters
                </Button>
                <Button variant="secondary" onClick={() => (window.location.href = '/me')}>
                  Employee view
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Pain points */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">What this replaces</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                BenefitsPro is built for the reality of enrollment season: spreadsheets, status-chasing, and last-minute fixes.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="h-full">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2">Spreadsheet tracking</h3>
                  <p className="text-muted-foreground">
                    Stop maintaining manual lists of who started, who finished, and who needs follow-up.
                  </p>
                </CardContent>
              </Card>
              <Card className="h-full">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2">Email ping-pong</h3>
                  <p className="text-muted-foreground">
                    Reduce the back-and-forth by giving employees a guided self-serve portal.
                  </p>
                </CardContent>
              </Card>
              <Card className="h-full">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2">Unclear enrollment status</h3>
                  <p className="text-muted-foreground">
                    Centralize enrollment steps so progress is visible and predictable.
                  </p>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </section>

        {/* How it works */}
        <section className="mt-16 bg-muted/30 rounded-2xl p-8 md:p-12">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">How HR teams use it</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                A simple flow from setup to invitations to completion tracking.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[ 
                {
                  icon: Building2,
                  title: '1) Company setup',
                  desc: 'Create your company profile and initialize enrollment.'
                },
                {
                  icon: Users,
                  title: '2) Add employees',
                  desc: 'Upload a census or add employees so invites can go out.'
                },
                {
                  icon: FileText,
                  title: '3) Send invites',
                  desc: 'Employees join using an invitation code tied to your organization.'
                },
                {
                  icon: TrendingUp,
                  title: '4) Track progress',
                  desc: 'See enrollment status and follow up where needed.'
                }
              ].map((step) => (
                <Card key={step.title} className="h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                      <step.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="text-base font-semibold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">HR FAQ</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Common questions when rolling out enrollment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  q: 'How do employees get access?',
                  a: 'Employees sign in and enter an invitation code provided by your organization.'
                },
                {
                  q: 'Do I need to be technical to set this up?',
                  a: 'No. The onboarding flow is guided and built for HR teams.'
                },
                {
                  q: 'Can employees request access without a code?',
                  a: 'Employees can’t self-attach to your company without a code; they’ll be prompted to contact HR.'
                },
                {
                  q: 'Where should I start?',
                  a: 'Start with company setup, then invite employees once your organization is ready.'
                }
              ].map((item) => (
                <Card key={item.q} className="h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-semibold mb-2">{item.q}</p>
                        <p className="text-muted-foreground">{item.a}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Final CTA */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="mt-4">
              <CardContent className="p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Start HR setup</h2>
                  <p className="text-muted-foreground">
                    Create your company account and begin onboarding.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  {featureFlags.allow_company_registration && (
                    <Button
                      className="w-full md:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                      onClick={() => (window.location.href = '/CompanySignup')}
                    >
                      Get started
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  <Button variant="outline" className="w-full md:w-auto" onClick={() => (window.location.href = '/why')}>
                    Read the company overview
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>
      </main>

      <footer className="bg-muted border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-foreground">© 2025 BenefitsPro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
